import { useMemo, useState, useEffect } from 'react'
import {
  CONNECTOR_ELEMENTS, actualVapourPressureFromDewPoint, addDays, beaufortForce, convert, dayOfYear, penmanMonteithDaily, unitByCode, windAt2m,
  type ForecastElementCode, type ForecastElementSeries, type ForecastView, type PenmanMonteithInput, type PenmanMonteithParts,
} from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { fmtNum } from '../lib/format.ts'
import { localDate, localDayLengthHours, localToInstant, formatLocal } from '../lib/time.ts'
import { useRpc } from './people.ts'

/** M24-W: the forecast's screens' arithmetic, pure (docs/M24.md F "The forecast's day and blocks", rules 1, 2, 6; ADR-126). Every number the Today card, the
 *  Prévision page, the water page's block and the timeline's forecast lanes print comes from here, from ONE answer of `api.forecast_view` (ForecastView).
 *
 *  THE RULE OF WHICH HOURS BELONG WHERE (the plan's, pinned by weatherForecast.test.ts):
 *  - An element with `accumulation_hours` (an hourly sum, maximum or probability — rain_1h, global_radiation_1h, wind_gust_1h, rain_probability_1h,
 *    thunder_probability_1h — and the 12- and 24-hour ones) valid at t covers the period (t − its hours, t]: MOSMIX's convention. So a window [from, to)
 *    takes the steps valid in (from, to]: a club day [00:00, 24:00) local (`lib/time.ts`, the site's zone) takes the steps valid from 01:00 to 24:00 — 23
 *    or 25 of them on a change of time — and rain that falls only in the 23:00–24:00 hour is that day's. A three-hour block likewise: its last hour is its own.
 *  - An instant (air_temperature, dew_point, wind_speed, wind_direction) keeps its valid time: a window [from, to) takes the steps valid in [from, to).
 *  - A 12-hour element (the 12-hour extremes, the ground minimum at 5 cm) is shown WITH ITS PERIOD (t − 12 h, t] ("de 19:00 à 07:00"); a day shows the one
 *    whose period ends in it (the night before), the next 24 hours the lowest whose period ends in them. The service's own evaporation covers (t − 24 h, t].
 *  - The forecast's own past is dropped: a window never starts before now (a period that ends after now is the current hour's, kept; an instant before now
 *    is gone), so today is always a partial day ("le reste du jour") and the first block is cut at now.
 *  - Chances are HOURLY: wherever they are gathered (24 h, a block, a day) the screen shows their maximum, labelled "chance horaire la plus haute"; the app
 *    never computes a chance for a period (the chance that it rains at least once is higher than any hour's).
 *  - A sum or an extreme says how many hours it holds (`n` of `of`) when the file left some of them empty; an element the point gives at no step is
 *    `notProvided` ("non fourni pour ce point"), never 0 and never left out.
 *  - ET0 PRÉVUE (FAO-56 Penman-Monteith, `penmanMonteithDaily`): only for a club day whose every hour is in the issue (not cut by now, the four elements
 *    air_temperature, dew_point, wind_speed and global_radiation_1h given at each of its steps), else "jour incomplet"; Tmin / Tmax the day's lowest /
 *    highest hourly temperature, ea by eq. 14 at the day's mean dew point, u2 by eq. 47 from the mean wind at 10 m (km/h → m/s through `convert`), Rs the
 *    sum of the day's radiation, the latitude and the altitude the club's parameters — never a default: a missing one is named.
 *  Nothing here is a verdict: no colour, no threshold, no window to work in, no comparison with an observed value (rules 1 and 2). */

export const HOUR_MS = 3_600_000
/** The Today card's span, the page's blocks and their span, the hour-by-hour fold, the days listed, the water page's days. */
export const FORECAST_NEXT_HOURS = 24
export const FORECAST_BLOCK_HOURS = 3
export const FORECAST_BLOCKS_SPAN_HOURS = 48
export const FORECAST_HOURLY_HOURS = 48
export const FORECAST_DAYS = 10
export const WATER_FORECAST_DAYS = 5
/** MOSMIX's wind is the 10-m wind (the element's label says so); eq. 47 brings it to 2 m. */
export const WIND_HEIGHT_M = 10
/** The four elements ET0 prévue needs at every hour of its day. */
export const ET0_ELEMENTS: readonly ForecastElementCode[] = ['air_temperature', 'dew_point', 'wind_speed', 'global_radiation_1h']
/** What the hour-by-hour fold shows of each hour, in this order (the 12- and 24-hour elements are in "Détails" with their periods). */
export const HOURLY_CODES: readonly ForecastElementCode[] = ['air_temperature', 'dew_point', 'rain_1h', 'rain_probability_1h', 'thunder_probability_1h', 'wind_speed', 'wind_gust_1h', 'wind_direction', 'global_radiation_1h']

type View = Pick<ForecastView, 'issue' | 'elements'>

/** The valid time (ms) of each step of the issue: `first_step_at + i × step_minutes`. */
export function stepTimes(view: Pick<ForecastView, 'issue'>): number[] {
  const first = Date.parse(view.issue.first_step_at), step = view.issue.step_minutes * 60_000
  return Array.from({ length: view.issue.step_count }, (_, i) => first + i * step)
}
/** Whether the issue still holds an hour after now (M24 review: the newest issue of a connector switched off for ten days covers none — it was received, its age
 *  shows, and the screens say it covers no hour to come instead of "no forecast received" or empty lists). */
export const coversFuture = (view: Pick<ForecastView, 'issue'>, now: number): boolean => stepTimes(view).some((at) => at > now)

/** A club day's label on the forecast's screens: the weekday in the reader's language and the date as every other date of these screens, dd/MM ("mercredi 28/10",
 *  "Wednesday 28/10": never the US month/day order beside 28/10 elsewhere). */
export function forecastDayLabel(date: string, lang: string, tz: string): string {
  const noon = Date.parse(localToInstant(date, '12:00', tz))
  return `${new Intl.DateTimeFormat(lang, { weekday: 'long', timeZone: tz }).format(new Date(noon))} ${formatLocal(noon, tz, 'dd/MM')}`
}

/** The local hour of each instant ("25/10 02:00"), with the season of its offset where the same label comes twice in the list (the autumn night repeats an hour:
 *  the first 02:00 is summer time, the second winter time) — never two identical lines that cannot be told apart. */
export function hourLabels(times: readonly number[], tz: string): Array<{ at: number; label: string; season: 'summer' | 'winter' | null }> {
  const rows = times.map((at) => ({ at, label: formatLocal(at, tz, 'dd/MM HH:mm'), offset: localOffsetMinutes(at, tz) }))
  const byLabel = new Map<string, number[]>()
  for (const r of rows) byLabel.set(r.label, [...(byLabel.get(r.label) ?? []), r.offset])
  return rows.map((r) => {
    const offsets = byLabel.get(r.label)!
    if (offsets.length < 2) return { at: r.at, label: r.label, season: null }
    return { at: r.at, label: r.label, season: r.offset === Math.max(...offsets) ? 'summer' : 'winter' }
  })
}
/** The zone's offset from UTC at an instant, in minutes (the wall clock minus UTC). */
const localOffsetMinutes = (at: number, tz: string): number => {
  const [d, hm] = formatLocal(at, tz, 'yyyy-MM-dd HH:mm').split(' ')
  return Math.round((Date.parse(`${d}T${hm}:00Z`) - Math.floor(at / 60_000) * 60_000) / 60_000)
}
export const elementOf = (view: View, code: ForecastElementCode): ForecastElementSeries | undefined => view.elements.find((e) => e.code === code)
export const isPeriod = (el: Pick<ForecastElementSeries, 'accumulation_hours'>): boolean => el.accumulation_hours !== null
/** Does a step valid at `at` belong to the window [from, to)? A period that ends at `at` does when `at` is in (from, to]; an instant when `at` is in [from, to). */
export const inWindow = (el: Pick<ForecastElementSeries, 'accumulation_hours'>, at: number, from: number, to: number): boolean => (isPeriod(el) ? at > from && at <= to : at >= from && at < to)

/** A local club day as a window [from, to) of instants (ms): local midnight to the next one, whatever the length of the day. */
export function dayWindow(date: string, tz: string): { from: number; to: number } {
  return { from: Date.parse(localToInstant(date, '00:00', tz)), to: Date.parse(localToInstant(addDays(date, 1), '00:00', tz)) }
}

/** A sum, an extreme or a mean over a window: its value and how many of the window's steps gave one (`n` of `of`); `none` when the window holds no value of a
 *  given element (an empty stretch of a series that is given elsewhere, or no step at all); `notProvided` when the point gives the element at no step. */
export type ForecastAgg = { kind: 'value'; value: number; n: number; of: number } | { kind: 'none'; of: number } | { kind: 'notProvided' }
export type AggHow = 'sum' | 'max' | 'min' | 'mean'
export function aggregate(view: View, times: readonly number[], code: ForecastElementCode, from: number, to: number, how: AggHow): ForecastAgg {
  const el = elementOf(view, code)
  if (!el || !el.provided) return { kind: 'notProvided' }
  let n = 0, of = 0, acc = how === 'max' ? -Infinity : how === 'min' ? Infinity : 0
  for (let i = 0; i < times.length; i++) {
    if (!inWindow(el, times[i]!, from, to)) continue
    of++
    const v = el.vals[i]
    if (v === null || v === undefined) continue
    n++
    acc = how === 'max' ? Math.max(acc, v) : how === 'min' ? Math.min(acc, v) : acc + v
  }
  if (n === 0) return { kind: 'none', of }
  return { kind: 'value', value: how === 'mean' ? acc / n : acc, n, of }
}
/** How many steps of the issue fall in the window by the period rule (the window's hours as the issue holds them). */
const periodSteps = (times: readonly number[], from: number, to: number): number => times.filter((at) => at > from && at <= to).length

/** A 12- or 24-hour value with the period it covers, (at − its hours, at]. */
export interface PeriodValue { at: number; from: number; to: number; value: number }
export type GroundMin = ({ kind: 'value' } & Omit<PeriodValue, 'at'>) | { kind: 'none' } | { kind: 'notProvided' }
/** Every value of a 12- or 24-hour element whose period ends after now, with its period (the page's "Détails"). */
export function periodValues(view: View, code: ForecastElementCode, o: { now: number }): PeriodValue[] {
  const el = elementOf(view, code)
  if (!el || !el.provided || el.accumulation_hours === null) return []
  const times = stepTimes(view), hours = el.accumulation_hours
  const out: PeriodValue[] = []
  times.forEach((at, i) => { const v = el.vals[i]; if (at > o.now && v !== null && v !== undefined) out.push({ at, from: at - hours * HOUR_MS, to: at, value: v }) })
  return out
}
/** The lowest ground minimum whose period ends in (from, to] (the earliest of equal ones), with its period. */
function groundMinIn(view: View, from: number, to: number): GroundMin {
  const el = elementOf(view, 'ground_temperature_min_12h')
  if (!el || !el.provided) return { kind: 'notProvided' }
  const all = periodValues(view, 'ground_temperature_min_12h', { now: from }).filter((p) => p.at <= to)
  if (all.length === 0) return { kind: 'none' }
  const low = all.reduce((m, p) => (p.value < m.value ? p : m))
  return { kind: 'value', value: low.value, from: low.from, to: low.to }
}

/** What a span of the forecast says, gathered: rain summed with its highest hourly chance, the thunder's highest hourly chance, the temperature range, the
 *  strongest mean wind with its Beaufort force, the strongest gust. */
export interface ForecastSpan {
  from: number; to: number
  rain: ForecastAgg; rainChance: ForecastAgg; thunderChance: ForecastAgg; tmin: ForecastAgg; tmax: ForecastAgg; wind: ForecastAgg; gust: ForecastAgg
  /** the Beaufort force of the strongest mean wind (`beaufortForce`, the WMO's bounds) */
  force: number | null
}
function span(view: View, times: readonly number[], from: number, to: number): ForecastSpan {
  const wind = aggregate(view, times, 'wind_speed', from, to, 'max')
  return {
    from, to,
    rain: aggregate(view, times, 'rain_1h', from, to, 'sum'),
    rainChance: aggregate(view, times, 'rain_probability_1h', from, to, 'max'),
    thunderChance: aggregate(view, times, 'thunder_probability_1h', from, to, 'max'),
    tmin: aggregate(view, times, 'air_temperature', from, to, 'min'),
    tmax: aggregate(view, times, 'air_temperature', from, to, 'max'),
    wind, gust: aggregate(view, times, 'wind_gust_1h', from, to, 'max'),
    force: wind.kind === 'value' ? beaufortForce(wind.value) : null,
  }
}

/** The Today card: the next 24 hours after now, and the night's ground minimum among them with its period. */
export interface NextHours extends ForecastSpan { groundMin: GroundMin }
export function nextHours(view: View, o: { now: number; hours?: number }): NextHours {
  const from = o.now, to = o.now + (o.hours ?? FORECAST_NEXT_HOURS) * HOUR_MS
  return { ...span(view, stepTimes(view), from, to), groundMin: groundMinIn(view, from, to) }
}

/** One three-hour block of the page: (from, to] on the site's clock (00–03, 03–06, …), cut at now for the first; `hours` the issue's steps in it (four in the
 *  autumn night that holds an hour twice, two in the spring one). */
export interface ForecastBlock extends ForecastSpan { cutFrom: number; partial: boolean; hours: number }
export function forecastBlocks(view: View, o: { now: number; tz: string; hours?: number }): ForecastBlock[] {
  const times = stepTimes(view)
  if (times.length === 0) return []
  const until = o.now + (o.hours ?? FORECAST_BLOCKS_SPAN_HOURS) * HOUR_MS, last = times[times.length - 1]!
  let date = localDate(o.now, o.tz)
  let hour = Math.floor(Number(formatLocal(o.now, o.tz, 'H')) / FORECAST_BLOCK_HOURS) * FORECAST_BLOCK_HOURS
  const at = (d: string, h: number) => Date.parse(localToInstant(d, `${String(h).padStart(2, '0')}:00`, o.tz))
  const out: ForecastBlock[] = []
  let from = at(date, hour)
  while (from < until && from < last) {
    hour += FORECAST_BLOCK_HOURS
    if (hour >= 24) { hour = 0; date = addDays(date, 1) }
    const to = at(date, hour)
    const cutFrom = Math.max(from, o.now)
    out.push({ ...span(view, times, cutFrom, to), from, to, cutFrom, partial: cutFrom > from || to > last, hours: periodSteps(times, cutFrom, to) })
    from = to
  }
  return out
}

/** One hour of the "heure par heure" fold: each hourly element's value as delivered (in its canonical unit), the period of an hourly accumulation. */
export interface ForecastHourValue { code: ForecastElementCode; value: number | null; unit: string; decimals: number; provided: boolean; period: { from: number; to: number } | null }
export interface ForecastHour { at: number; i: number; values: ForecastHourValue[] }
export function forecastHours(view: View, o: { now: number; hours?: number }): ForecastHour[] {
  const until = o.now + (o.hours ?? FORECAST_HOURLY_HOURS) * HOUR_MS
  const els = HOURLY_CODES.map((c) => elementOf(view, c)).filter((e): e is ForecastElementSeries => !!e)
  return stepTimes(view).flatMap((at, i) => (at > o.now && at <= until ? [{
    at, i,
    values: els.map((e) => ({ code: e.code, value: e.vals[i] ?? null, unit: e.unit, decimals: e.decimals, provided: e.provided, period: e.accumulation_hours ? { from: at - e.accumulation_hours * HOUR_MS, to: at } : null })),
  }] : []))
}

/** What ET0 prévue was worked from (shown beside it, as for Hargreaves-Samani). */
export interface Et0Inputs {
  tminC: number; tmaxC: number; meanDewPointC: number; eaKpa: number; meanWindKmh: number; u2Ms: number | null; rsMj: number
  latitudeDeg: number | null; altitudeM: number | null; doy: number
}
export type ForecastEt0 =
  | { status: 'incomplete'; lacking: ForecastElementCode[] }
  | { status: 'missing'; missing: PenmanMonteithInput[]; invalid: PenmanMonteithInput[]; inputs: Et0Inputs }
  | { status: 'ok'; et0: number; inputs: Et0Inputs; parts: PenmanMonteithParts }

/** One club day of the forecast: what it says over the day's remaining hours, the ground minimum of the night before it, and ET0 prévue. */
export interface ForecastDay extends ForecastSpan {
  /** `from` is the day's local midnight, `to` the next one; the figures are over [cutFrom, to) */
  date: string
  /** the day's window cut at now (today: the rest of the day) */
  cutFrom: number
  /** 23, 24 or 25 */
  dayHours: number
  /** the issue's steps in the (cut) day, by the period rule */
  hours: number
  /** cut by now, or the issue does not hold every hour of it */
  partial: boolean
  groundMin: GroundMin
  et0: ForecastEt0
}
export function forecastDays(view: View, o: { now: number; tz: string; latitude: number | null; altitude: number | null; days?: number }): ForecastDay[] {
  const times = stepTimes(view)
  const dates = new Set<string>()
  for (const at of times) { if (at > o.now) dates.add(localDate(at - 1, o.tz)); if (at >= o.now) dates.add(localDate(at, o.tz)) }
  const perStep = 60 / view.issue.step_minutes
  return [...dates].sort().slice(0, o.days ?? FORECAST_DAYS).map((date) => {
    const w = dayWindow(date, o.tz)
    const cutFrom = Math.max(w.from, o.now)
    const dayHours = localDayLengthHours(date, o.tz)
    const hours = periodSteps(times, cutFrom, w.to)
    const partial = cutFrom > w.from || hours < dayHours * perStep
    // the span's figures are over the cut window; `from` stays the day's own start (local midnight), `cutFrom` where the remaining hours begin
    return { ...span(view, times, cutFrom, w.to), from: w.from, date, cutFrom, dayHours, hours, partial, groundMin: groundMinIn(view, cutFrom, w.to), et0: dayEt0(view, times, date, w, partial, dayHours * perStep, o) }
  })
}

function dayEt0(view: View, times: readonly number[], date: string, w: { from: number; to: number }, partial: boolean, expected: number, o: { latitude: number | null; altitude: number | null }): ForecastEt0 {
  const a = Object.fromEntries(ET0_ELEMENTS.map((c) => [c, aggregate(view, times, c, w.from, w.to, c === 'global_radiation_1h' ? 'sum' : 'mean')])) as Record<string, ForecastAgg>
  const lacking = ET0_ELEMENTS.filter((c) => { const x = a[c]!; return x.kind !== 'value' || x.n !== expected || x.of !== expected })
  if (partial || lacking.length > 0) return { status: 'incomplete', lacking }
  const v = (c: ForecastElementCode) => (a[c] as { value: number }).value
  const tmin = (aggregate(view, times, 'air_temperature', w.from, w.to, 'min') as { value: number }).value
  const tmax = (aggregate(view, times, 'air_temperature', w.from, w.to, 'max') as { value: number }).value
  const meanWindKmh = v('wind_speed'), meanDewPointC = v('dew_point')
  const inputs: Et0Inputs = {
    tminC: tmin, tmaxC: tmax, meanDewPointC, eaKpa: actualVapourPressureFromDewPoint(meanDewPointC), meanWindKmh,
    u2Ms: windAt2m(Number(convert(String(meanWindKmh), 'km_h', 'm_s')), WIND_HEIGHT_M), rsMj: v('global_radiation_1h'),
    latitudeDeg: o.latitude, altitudeM: o.altitude, doy: dayOfYear(date),
  }
  const r = penmanMonteithDaily({ tminC: inputs.tminC, tmaxC: inputs.tmaxC, eaKpa: inputs.eaKpa, u2Ms: inputs.u2Ms, rsMj: inputs.rsMj, latitudeDeg: o.latitude, altitudeM: o.altitude, doy: inputs.doy })
  if (r.et0 === null || r.parts === null) return { status: 'missing', missing: r.missing, invalid: r.invalid, inputs }
  return { status: 'ok', et0: r.et0, inputs, parts: r.parts }
}

/** The issue's age when shown: the server's own difference (`as_of` − `issued_at`, its clock), grown by the time the answer has been on the device; "ancienne"
 *  strictly beyond the connector's `stale_after` (the server's rule, `integ.v_freshness`). A device clock that moved back never makes it younger. */
export function forecastAge(view: Pick<ForecastView, 'as_of' | 'issue' | 'connector'>, now: number, receivedAt: number): { ms: number; stale: boolean; staleAfterHours: number } {
  const ms = Date.parse(view.as_of) - Date.parse(view.issue.issued_at) + Math.max(0, now - receivedAt)
  return { ms, stale: ms > view.connector.stale_after_hours * HOUR_MS, staleAfterHours: view.connector.stale_after_hours }
}

/** Whether the provider gave any of these elements in another unit than the one shown (K → °C, m/s → km/h, kJ/m² → MJ/m²): the `source` line then adds
 *  "(unités converties)". From the mapping's twin (`CONNECTOR_ELEMENTS`: the unit the file gives), never a guess. */
export function unitsConverted(view: Pick<ForecastView, 'connector' | 'elements'>, codes: readonly ForecastElementCode[]): boolean {
  return codes.some((c) => {
    const el = view.elements.find((e) => e.code === c)
    const map = CONNECTOR_ELEMENTS.find((m) => m.kind === view.connector.kind && m.element_code === c)
    return !!el && !!map && map.unit_given !== el.unit
  })
}

/** The server's answer, read online only (the forecast is in no sync profile: `api.forecast_view`, forecast.read): `undefined` until it has answered, `null`
 *  when the site has no forecast (no connector, or no issue yet). `receivedAt` is the device's instant of the answer (the age grows from it). Never written to
 *  IndexedDB: it lives in the page's memory while the page is open (docs/M24.md B, "Not mirrored"). */
export interface ForecastRead { view: ForecastView | null | undefined; error: string | null; online: boolean; receivedAt: number; reload: () => Promise<void> }
export function useForecast(enabled: boolean): ForecastRead {
  const { org, site } = useClub()
  const r = useRpc<ForecastView | null>('forecast_view', { p_org: org.org_id, p_site: site.site_id }, enabled)
  const [receivedAt, setReceivedAt] = useState(() => Date.now())
  // the last answer stays while the page is open (a later failure is said beside it, never by emptying the card)
  useEffect(() => { if (r.fresh) setReceivedAt(Date.now()) }, [r.fresh, r.data])
  return useMemo(() => ({ view: r.fresh ? r.data : undefined, error: r.error, online: r.online, receivedAt, reload: r.reload }), [r.fresh, r.data, r.error, r.online, receivedAt, r.reload])
}

// ---- the words of a figure (pure; the screens pass `t` and the language) -----------------------------------------------------------------------------------
type T = (key: string, o?: Record<string, unknown>) => string
/** A unit's symbol from the unit table's twin (M9: the symbols are typed once, in `ref.unit` / `UNITS`). */
export const symbolOf = (unit: string, lang: string): string => unitByCode(unit)?.symbol[lang.startsWith('en') ? 'en' : 'fr'] ?? unit
/** "2,4 mm", "60 %", "240°": at most the element's decimals, the trailing zero dropped (`fmtNum`, CLAUDE.md M14); a no-break space keeps the unit on the number's
 *  line (a phone wrapped "14" and "%" apart). */
export const NBSP = '\u00a0'
export const quantityText = (v: number, unit: string, decimals: number, lang: string): string => {
  const s = symbolOf(unit, lang)
  return s === '°' ? `${fmtNum(v, lang, decimals)}°` : `${fmtNum(v, lang, decimals)}${NBSP}${s}`
}
/** A gathered value as words: the value (with "(n h sur m)" when the file left some of its hours empty), "pas de valeur", or "non fourni pour ce point". */
export function aggText(a: ForecastAgg, unit: string, decimals: number, lang: string, t: T): string {
  if (a.kind === 'notProvided') return t('forecast.notProvided')
  if (a.kind === 'none') return t('forecast.noValue')
  return `${quantityText(a.value, unit, decimals, lang)}${a.n < a.of ? ` ${t('forecast.hoursOf', { n: a.n, of: a.of })}` : ''}`
}
/** An element's display decimals and unit, from the answer (the element table's, via api.forecast_view). */
export const decimalsOf = (view: View, code: ForecastElementCode): number => elementOf(view, code)?.decimals ?? 1
export const unitOf = (view: View, code: ForecastElementCode): string => elementOf(view, code)?.unit ?? ''
/** "2 h", "25 min": how old, never rounded up. */
export const ageText = (ms: number, t: T): string => (ms < HOUR_MS ? t('forecast.ageMinutes', { count: Math.max(0, Math.floor(ms / 60_000)) }) : t('forecast.ageHours', { count: Math.floor(ms / HOUR_MS) }))
/** A period on the site's clock: "de 19:00 à 07:00" when it lasts at most a day, "du 27/10 07:00 au 28/10 07:00" for a longer one or when `withDates`. */
export function periodText(from: number, to: number, tz: string, t: T, withDates = false): string {
  if (!withDates && to - from <= 24 * HOUR_MS) return t('forecast.period', { from: formatLocal(from, tz, 'HH:mm'), to: formatLocal(to, tz, 'HH:mm') })
  return t('forecast.periodDays', { from: formatLocal(from, tz, 'dd/MM HH:mm'), to: formatLocal(to, tz, 'dd/MM HH:mm') })
}
/** "6 à 13,7 °C": a lowest and a highest value in one unit, the unit once. */
export const rangeText = (min: number, max: number, unit: string, decimals: number, lang: string, t: T): string =>
  t('forecast.range', { min: fmtNum(min, lang, decimals), max: quantityText(max, unit, decimals, lang) })
/** "chance horaire la plus haute 60 %" (an hourly maximum is never labelled as the period's chance), or the words of a missing value. */
export const chanceText = (a: ForecastAgg, lang: string, t: T): string => (a.kind === 'value' ? t('forecast.chanceHourlyMax', { pct: quantityText(a.value, 'pct', 0, lang) }) : aggText(a, 'pct', 0, lang, t))
